<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineExpose, ref, computed } from 'vue';

/*==================================================================*/
/*===================== IMPORTS UTILS ==============================*/
/*==================================================================*/

import UtilID from '@/utils/UtilID.js';
import UtilObject from '@/utils/UtilObject';
import UtilDateTime from '@/utils/UtilDateTime';
import UtilMoney from '@/utils/UtilMoney';

/*==================================================================*/
/*================ IMPORTS COMPONENTES =============================*/
/*==================================================================*/

import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ConteudoModal from './ConteudoModal.vue';

/*==================================================================*/
/*================ IMPORTS SERVICES ================================*/
/*==================================================================*/

import OrdemDePagamento from '@/api/v2/services/OrdemDePagamentoService';

/*==================================================================*/
/*================== STORE DE COLABORADOR ==========================*/
/*==================================================================*/

import useColaboradorStore from '@/api/v2/stores/ColaboradorStore.js';
const colaboradorStore = useColaboradorStore();

/*==================================================================*/
/*==================== STORE DE USUARIO ============================*/
/*==================================================================*/

import useUsuarioStore from '@/api/v2/stores/UsuarioStore.js';
import UtilFile from '@/utils/UtilFile';
const usuarioStore = useUsuarioStore();

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    ordemDePagamentoRequisitantes: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoCredorColaboradores: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoCredorFornecedores: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoDespesas: {
        type: Array,
        default: () => []
    },
    ordemDePagamentoJobs: {
        type: Array,
        default: () => []
    },
    onClose: {
        type: Function,
        default: null
    }
});

/*==================================================================*/
/*================== MODAL FULLSCREEN REF ==========================*/
/*==================================================================*/

const modalFullscreenRef = ref();

/*==================================================================*/
/*================== DADO ==========================================*/
/*==================================================================*/

const dado = ref({});

/*==================================================================*/
/*=================== COMPONENTS REFS ==============================*/
/*==================================================================*/

const conteudoModalRef = ref(null);

/*==================================================================*/
/*================== PAGAMENTO =====================================*/
/*==================================================================*/

const pagamento = computed(() => {

    return dado?.value?.parcelas?.[0] || {};

});

/*==================================================================*/
/*================== DISABLED CRIAR ORDEM DE PAGAMENTO =============*/
/*==================================================================*/

const disabledCriarOrdemDePagamento = computed(() => {

    // Desabilita o botão se o modal estiver desativado.
    if(props.disabled){
        return true;
    }

    // Desabilita se algum cartão não for válido
    if (conteudoModalRef.value?.isValid === false) {
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== ALTERA DADO INTERNO ===========================*/
/*==================================================================*/

const alteraDado = (update = {}) => {

    /*==================================================================*/
    /*====================== ALTERA DADO ===============================*/
    /*==================================================================*/

    let dadoUpdate = UtilObject.merge(dado.value, update);

    /*==================================================================*/
    /*====================== ATUALIZA PAGAMENTO ========================*/
    /*==================================================================*/

    let pagamentoUpdate = pagamento.value;
    pagamentoUpdate.valor = dadoUpdate?.valorTotal ?? 0,
    pagamentoUpdate.dataDoPagamento = dadoUpdate?.dataPrevisaoPagamento ?? '',

    dadoUpdate.parcelas = [pagamentoUpdate];

    /*==================================================================*/
    /*======================= SALTA ALTERAÇÃO ==========================*/
    /*==================================================================*/

    dado.value = dadoUpdate;

};

/*==================================================================*/
/*================== RECALCULA VALOR TOTAL DAS DESPESAS ============*/
/*==================================================================*/

const recalculaValorTotalDespesas = (despesas = []) => {

    let valorTotal = despesas?.reduce((acc, despesa = {}) => {

        return acc + UtilMoney.toNumber(despesa?.valor);

    }, 0);

    return valorTotal;

};

/*==================================================================*/
/*================== ADICIONA DESPESA ==============================*/
/*==================================================================*/

const adicionaDespesa = () => {

    let despesas = dado?.value?.despesas ?? [];

    despesas.push({
        id: UtilID.generateDateTimeID(),
        despesa: '',
        dataDaDespesa: '',
        dataCriacao: UtilDateTime.todayUTC(),
        valor: 0,
        veicular: false,
        veiculoTipo: "",
        veiculoPlaca: "",
        veiculoKMInicial: 0,
        veiculoKMFinal: 0,
        observacao: '',
        anexos: [],
        
    });

    alteraDado({
        despesas: despesas,
    });

};

/*==================================================================*/
/*================== ALTERA DESPESA ================================*/
/*==================================================================*/

const alteraDespesa = (update = {}) => {

    let despesas = dado?.value?.despesas?.map((despesa = {}) => {

        if(despesa?.id != update?.id){
            return despesa;
        }

        return UtilObject.merge(despesa, update);

    }) ?? [];

    let valorTotal = recalculaValorTotalDespesas(despesas);

    alteraDado({
        despesas: despesas,
        valorTotal: valorTotal,
    });

};

/*==================================================================*/
/*================== DESCARTA DESPESA ==============================*/
/*==================================================================*/

const descartaDespesa = (update = {}) => {

    let despesas = dado?.value?.despesas?.filter((despesa = {}) => despesa?.id != update?.id) ?? [];

    let valorTotal = recalculaValorTotalDespesas(despesas);

    alteraDado({
        despesas: despesas,
        valorTotal: valorTotal,
    });

};

/*==================================================================*/
/*================== ADICIONA PARCELA ==============================*/
/*==================================================================*/

const adicionaParcela = () => {

    let parcelas = dado?.value?.parcelas ?? [];

    parcelas.push({
        id: UtilID.generateDateTimeID(),
        descricao: 'Pagamento de Ordem de Pagamento',
        dataCriacao: UtilDateTime.todayUTC(),
        dataDoPagamento: '',
        valor: 0,
    });

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== ALTERA PARCELA ================================*/
/*==================================================================*/

const alteraParcela = (update = {}) => {

    let parcelas = dado?.value?.parcelas?.map((parcela = {}) => {

        if(parcela?.id != update?.id){
            return parcela;
        }

        return UtilObject.merge(parcela, update);

    }) ?? [];

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== DESCARTA PARCELA ==============================*/
/*==================================================================*/

const descartaParcela = (update = {}) => {

    let parcelas = dado?.value?.parcelas?.filter((parcela = {}) => parcela?.id != update?.id) ?? [];

    alteraDado({
        parcelas: parcelas,
    });

};

/*==================================================================*/
/*================== CRIAR ORDEM DE PAGAMENTO ======================*/
/*==================================================================*/

const criarOrdemDePagamento = async () => {
    
    let despesas = [];

    for(const despesa of dado?.value?.despesas ?? []){

        let despesaAUX = {
            despesa: despesa?.despesa?.id || '',
            dataCriacao: despesa?.dataCriacao || '',
            dataDaDespesa: despesa?.dataDaDespesa || '',
            valor: despesa?.valor || 0,
            veicular: despesa?.veicular || false,
            veiculoTipo: despesa?.veiculoTipo || '',
            veiculoPlaca: despesa?.veiculoPlaca || '',
            veiculoKMInicial: despesa?.veiculoKMInicial || 0,
            veiculoKMFinal: despesa?.veiculoKMFinal || 0,
            observacao: despesa?.observacao ?? '',
            anexos: await UtilFile.manyToBase64(despesa?.anexos ?? []),
        }

        despesas.push(despesaAUX);

    }

    const parcelas = dado.value?.parcelas?.map(parcela => ({
        descricao: parcela?.descricao || '',
        dataCriacao: parcela?.dataCriacao || '',
        dataDoPagamento: parcela?.dataDoPagamento || '',
        valor: parcela?.valor || 0,
    }));

    const ordemDePagamentoValores = {
        tipo: dado.value?.tipo || "",
        emitente: dado.value?.emitente?.id || "",
        requisitante: dado.value?.requisitante?.id || "",
        notaFiscal: dado.value?.notaFiscal || "",
        tipoCredor: dado.value?.tipoCredor || "",
        credorColaborador: dado.value?.credorColaborador?.id || "",
        credorFornecedor: dado.value?.credorFornecedor?.id || "",
        formaDePagamento: dado.value?.formaDePagamento || "",
        justificativa: dado.value?.justificativa || "",
        valorTotal: dado.value?.valorTotal || 0,
        recorrencia: dado.value?.recorrencia || false,
        recorrenciaQuantidade: dado.value?.recorrenciaQuantidade || 0,
        dataCriacao: dado.value?.dataCriacao || "",
        dataPrevisaoPagamento: dado.value?.dataPrevisaoPagamento || "",
        job: dado.value?.job?.id || "",
        ordemDeServico: dado.value?.ordemDeServico?.id || "",
        despesas: despesas || [],
        parcelas: parcelas || [],
    };
    
    // Chama o service para criar a ordem de pagamento
    let response = await OrdemDePagamento.criaOrdemDePagamento(ordemDePagamentoValores);
    if(response?.status != 200) {
        throw new Error("Falha ao criar Ordem de Pagamento");
    }

    // Fechar modal após sucesso
    await fecharModal();

};

/*==================================================================*/
/*================== ABRIR MODAL ===================================*/
/*==================================================================*/

const abrirModal = async () => {

    preparaModal();
    preparaPagamento();

    await modalFullscreenRef.value?.abrirModal();

};

/*==================================================================*/
/*================== FECHAR MODAL ==================================*/
/*==================================================================*/

const fecharModal = async () => {

    await modalFullscreenRef.value?.fecharModal();

};

/*==================================================================*/
/*================== AO FECHAR MODAL ===============================*/
/*==================================================================*/

const aoFecharModal = async () => {

    await props.onClose?.();

};

/*==================================================================*/
/*================== PREPARA PAGAMENTO =============================*/
/*==================================================================*/

const preparaPagamento = () => {

    adicionaParcela();

};

/*==================================================================*/
/*================== PREPARA MODAL =================================*/
/*==================================================================*/

const preparaModal = () => {

    dado.value = {
        id: UtilID.generateDateTimeID(),
        tipo: 'Outras Despesas',
        emitente: {
            id: colaboradorStore?.colaborador?.id,
            codigo: colaboradorStore?.colaborador?.codigo,
            nome: colaboradorStore?.colaborador?.nome,
            grupo: usuarioStore?.usuario?.grupoNome,
            usuario: usuarioStore?.usuario?.id
        },
        requisitante: {},
        notaFiscal: '',
        tipoCredor: '',
        credorColaborador: {},
        credorFornecedor: {},
        formaDePagamento: '',
        justificativa: '',
        valorTotal: 0,
        recorrencia: false,
        recorrenciaQuantidade: 0,
        dataCriacao: UtilDateTime.todayUTC(),
        dataPrevisaoPagamento: '',
        job: {},
        ordemDeServico: {},
        despesas: [],
        parcelas: [],
    };

};

/*==================================================================*/
/*================== EXPOSE ========================================*/
/*==================================================================*/

defineExpose({ abrirModal, fecharModal });

</script>

<template>

    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="props.disabled"
        @onClose="aoFecharModal"
    >

        <template #modal-navegacao-titulo>

            Criar Ordem de Pagamento

        </template>

        <template #modal-navegacao-botoes>

            <BotaoTextoBase
                :disabled="disabledCriarOrdemDePagamento"
                :click="criarOrdemDePagamento"
            >
                Criar Ordem de Pagamento
            </BotaoTextoBase>

        </template>

        <template #modal-conteudo>

            <ConteudoModal
                ref="conteudoModalRef"
                :disabled="props.disabled"
                :dado="dado"
                :pagamento="pagamento"
                :ordemDePagamentoRequisitantes="props.ordemDePagamentoRequisitantes"
                :ordemDePagamentoCredorColaboradores="props.ordemDePagamentoCredorColaboradores"
                :ordemDePagamentoCredorFornecedores="props.ordemDePagamentoCredorFornecedores"
                :ordemDePagamentoDespesas="props.ordemDePagamentoDespesas"
                :ordemDePagamentoJobs="props.ordemDePagamentoJobs"
                @update:alteraDado="alteraDado"
                @update:adicionaDespesa="adicionaDespesa"
                @update:alteraDespesa="alteraDespesa"
                @update:descartaDespesa="descartaDespesa"
                @update:adicionaParcela="adicionaParcela"
                @update:alteraParcela="alteraParcela"
                @update:descartaParcela="descartaParcela"
            />

        </template>

    </ModalFullscreen>
    
</template>

<style scoped>
</style>